import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { validateAnswer } from '../../utils/answerValidator';
import { generateCodingTask, generateMCQ, validateGeneratedTask, getGeneratedTasks } from '../../services/api';
import { formatInstructions, formatExpectedOutput } from '../../utils/contentFormatter';
import CodeEditor from '../CodeEditor/CodeEditor';
import './McqScreen.css';

const McqScreen = ({ screen, onAnswer, userResponse, showSolution, lessonId, subjectArea, screenState }) => {
  const { handleRequest } = useApi();
  const [selectedAnswer, setSelectedAnswer] = useState(userResponse || null);
  const [validationResult, setValidationResult] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(!!userResponse || !!screenState?.answered);
  const [solutionShown, setSolutionShown] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState({});
  const [showCodingTask, setShowCodingTask] = useState(false);
  const [showMCQ, setShowMCQ] = useState(false);
  const [loading, setLoading] = useState({ coding: false, mcq: false });
  const [code, setCode] = useState('');
  const [codeValidationResult, setCodeValidationResult] = useState(null);

  // Parse options from contentForTeacherOrSystem
  // Format: Question text\nOption1\nOption2\nOption3\nOption4
  const parseQuestion = () => {
    const content = screen.contentForTeacherOrSystem || '';
    const lines = content.split('\n').filter(line => line.trim());
    const question = lines[0] || '';
    const options = lines.slice(1) || [];
    return { question, options };
  };

  const { question, options } = parseQuestion();

  useEffect(() => {
    // Load validation result from screenState if available
    if (screenState?.answered && screenState.feedback) {
      setValidationResult({
        isCorrect: screenState.isCorrect,
        feedback: screenState.feedback
      });
    }
  }, [screenState]);

  useEffect(() => {
    // Reset state when screen changes
    setGeneratedTasks({});
    setShowCodingTask(false);
    setShowMCQ(false);
    setCode('');
    setCodeValidationResult(null);
    
    // Load existing generated tasks for this screen
    if (lessonId && screen.id) {
      handleRequest(() => getGeneratedTasks(lessonId, screen.id))
        .then(response => {
          const tasks = response.data.tasks || [];
          const tasksMap = {};
          tasks.forEach(t => {
            tasksMap[t.type] = t.task;
          });
          setGeneratedTasks(tasksMap);
          if (tasksMap.coding) setShowCodingTask(true);
          if (tasksMap.mcq) setShowMCQ(true);
        })
        .catch(err => console.error('Kunne ikke laste genererte oppgaver:', err));
    }
  }, [lessonId, screen.id, handleRequest]);

  const handleGenerateCodingTask = async () => {
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(generateCodingTask, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, coding: response.data.task }));
      setShowCodingTask(true);
    } catch (error) {
      console.error('Kunne ikke generere kodeoppgave:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleGenerateMCQ = async () => {
    setLoading(prev => ({ ...prev, mcq: true }));
    try {
      const response = await handleRequest(generateMCQ, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, mcq: response.data.question }));
      setShowMCQ(true);
    } catch (error) {
      console.error('Kunne ikke generere flervalgsspørsmål:', error);
    } finally {
      setLoading(prev => ({ ...prev, mcq: false }));
    }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) return;
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(validateGeneratedTask, lessonId, {
        screenId: screen.id,
        taskType: 'coding',
        answer: code
      });
      setCodeValidationResult(response.data);
      if (onAnswer) {
        onAnswer(code, response.data);
      }
    } catch (error) {
      console.error('Kunne ikke validere kode:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleMCQAnswer = async (answer, result) => {
    if (onAnswer) {
      onAnswer(answer, result);
    }
  };

  // Get the correct answer option text
  const getCorrectAnswerOption = () => {
    const checkingLogic = screen.checkingLogic || {};
    const expectedAnswer = checkingLogic.expectedAnswer;
    
    if (!expectedAnswer) return null;
    
    // Try to find option that starts with the expected answer (e.g., "A" or "A)")
    const normalizedExpected = expectedAnswer.toString().trim().toUpperCase();
    const correctOption = options.find(opt => {
      const optUpper = opt.trim().toUpperCase();
      return optUpper.startsWith(normalizedExpected + ')') || optUpper.startsWith(normalizedExpected + ' ');
    });
    
    return correctOption || options[0]; // Fallback to first option if not found
  };

  const correctAnswerOption = getCorrectAnswerOption();

  const handleSelect = (option) => {
    if (hasAnswered) return; // Prevent changing answer after submission
    setSelectedAnswer(option);
  };

  const handleSubmit = () => {
    if (!selectedAnswer) return;

    const checkingLogic = screen.checkingLogic || {};
    
    // Extract the letter prefix from the selected option (e.g., "A" from "A) HelloWorld")
    const getOptionPrefix = (optionText) => {
      const match = optionText.trim().match(/^([A-Za-z])[\)\.]/);
      return match ? match[1].toUpperCase() : null;
    };
    
    const selectedPrefix = getOptionPrefix(selectedAnswer);
    const expectedAnswer = checkingLogic.expectedAnswer?.toString().trim().toUpperCase();
    
    // Check if the selected option's prefix matches the expected answer
    let isCorrect = false;
    
    if (selectedPrefix && expectedAnswer) {
      isCorrect = selectedPrefix === expectedAnswer;
    }
    
    // Also check acceptable answers
    if (!isCorrect && checkingLogic.acceptableAnswers && Array.isArray(checkingLogic.acceptableAnswers)) {
      isCorrect = checkingLogic.acceptableAnswers.some(acc => {
        const accUpper = acc.toString().trim().toUpperCase();
        return selectedPrefix === accUpper;
      });
    }
    
    const feedback = isCorrect 
      ? (checkingLogic.feedbackCorrect || 'Correct!')
      : (checkingLogic.feedbackIncorrect || 'Not quite right. Try again!');
    
    const result = {
      isCorrect,
      feedback,
      score: isCorrect ? 100 : 0
    };
    
    setValidationResult(result);
    setHasAnswered(true);
    
    if (onAnswer) {
      onAnswer(selectedAnswer, result);
    }
  };

  // Handle show solution
  React.useEffect(() => {
    if (showSolution && !solutionShown) {
      setSolutionShown(true);
      setHasAnswered(true); // Mark as answered so correct answer is highlighted
    }
  }, [showSolution, solutionShown]);

  return (
    <div className="mcq-screen">
      <div className="mcq-content">
        <div className="question-panel">
          <h3 className="question-text">{question}</h3>
          
          <div className="options-list">
            {options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrectAnswer = correctAnswerOption === option && (solutionShown || (validationResult?.isCorrect && isSelected));
              const isCorrect = validationResult?.isCorrect && isSelected;
              const isIncorrect = !validationResult?.isCorrect && isSelected && hasAnswered && !solutionShown;
              
              return (
                <button
                  key={index}
                  className={`option-button ${isSelected ? 'selected' : ''} ${isCorrect ? 'correct' : ''} ${isIncorrect ? 'incorrect' : ''} ${isCorrectAnswer && solutionShown ? 'correct-answer-shown' : ''}`}
                  onClick={() => handleSelect(option)}
                  disabled={hasAnswered}
                >
                  {option}
                </button>
              );
            })}
          </div>

          {validationResult && (
            <div className={`feedback ${validationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
              {validationResult.feedback}
            </div>
          )}

          {!hasAnswered && selectedAnswer && (
            <button
              className="submit-answer-button"
              onClick={handleSubmit}
            >
              {screen.uiLayoutHints?.primaryButtonLabel || 'Sjekk svar'}
            </button>
          )}

          {/* Generate Task Buttons */}
          {((subjectArea === 'programming' && !showCodingTask) || (!showMCQ && !generatedTasks.mcq)) && (
            <div className="generate-buttons">
              {subjectArea === 'programming' && !showCodingTask && (
                <button
                  className="generate-button generate-coding"
                  onClick={handleGenerateCodingTask}
                  disabled={loading.coding}
                >
                  {loading.coding ? 'Genererer...' : 'Generer kodeoppgave'}
                </button>
              )}
              {!showMCQ && !generatedTasks.mcq && (
                <button
                  className="generate-button generate-mcq"
                  onClick={handleGenerateMCQ}
                  disabled={loading.mcq}
                >
                  {loading.mcq ? 'Genererer...' : 'Generer flervalgsspørsmål'}
                </button>
              )}
            </div>
          )}

          {/* Generated Coding Task */}
          {subjectArea === 'programming' && showCodingTask && generatedTasks.coding && (
            <div className="generated-task coding-task">
              <h3>Kodeoppgave</h3>
              <p><strong>Beskrivelse:</strong> {generatedTasks.coding.description}</p>
              <p><strong>Instruksjoner:</strong> <span dangerouslySetInnerHTML={{ __html: formatInstructions(generatedTasks.coding.instructions) }} /></p>
              {generatedTasks.coding.expectedOutput && (
                <div className="expected-output-generated">
                  <strong>Forventet resultat:</strong>
                  <p dangerouslySetInnerHTML={{ __html: formatExpectedOutput(generatedTasks.coding.expectedOutput) }} />
                </div>
              )}
              <CodeEditor
                value={code}
                onChange={setCode}
                language="javascript"
                height="300px"
              />
              {codeValidationResult && (
                <div className={`feedback ${codeValidationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
                  {codeValidationResult.feedback}
                </div>
              )}
              <button
                className="submit-code-button"
                onClick={handleSubmitCode}
                disabled={loading.coding || !code.trim()}
              >
                {loading.coding ? 'Validerer...' : 'Kjør kode'}
              </button>
            </div>
          )}

          {/* Generated MCQ */}
          {showMCQ && generatedTasks.mcq && (
            <div className="generated-task mcq-task">
              <McqScreen
                screen={generatedTasks.mcq}
                onAnswer={handleMCQAnswer}
                lessonId={lessonId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default McqScreen;
